/**
 * Orders behind a report row (a doctor, a patient or a service) in the selected period.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowForward as OpenIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { StatusChip } from '../common';
import { useGetOrdersQuery } from '../../store/api/ordersApi';
import type { ReportPeriod } from '../../store/api/detailedReportsApi';
import { formatCurrency } from '../../utils/format';

export interface ReportOrdersFilter {
  provider_type?: 'SPECIALIST' | 'CONSULTANT';
  provider_id?: number;
  patient_id?: number;
  subservice_id?: number;
}

interface ReportOrdersDialogProps {
  open: boolean;
  title: string;
  filter: ReportOrdersFilter;
  period: ReportPeriod;
  onClose: () => void;
}

const PAGE_SIZE = 10; // backend page size

const ReportOrdersDialog = ({ open, title, filter, period, onClose }: ReportOrdersDialogProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const { data, isFetching } = useGetOrdersQuery({ ...filter, ...period, page: page + 1 }, { skip: !open });
  const orders = data?.results ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {title}
        <Typography variant="body2" color="text.secondary">
          {period.date_from || period.date_to
            ? t('reports.detailed.periodLabel', { from: period.date_from || '…', to: period.date_to || '…' })
            : t('reports.detailed.allTime')}
          {data ? ` · ${t('reports.detailed.rowsCount', { count: data.count })}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['orderNumber', 'requestedAt', 'patient', 'service', 'provider', 'scheduled', 'status', 'amount', 'rating', ''].map(
                  (key) => (
                    <TableCell key={key} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {key ? t(`reports.detailed.orderCols.${key}`) : ''}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    {t('common.loading')}
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {t('reports.detailed.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{order.order_number}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{moment(order.created_at).format('DD MMM YYYY HH:mm')}</TableCell>
                    <TableCell>{order.patient_name}</TableCell>
                    <TableCell>{order.subservice_name || order.service_name}</TableCell>
                    <TableCell>{order.assigned_provider?.name || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {order.scheduled_date ? moment(order.scheduled_date).format('DD MMM YYYY') : '—'}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={order.status} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(Number(order.total_amount))}</TableCell>
                    <TableCell>{order.service_rating ? `★ ${order.service_rating}` : '—'}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        endIcon={<OpenIcon fontSize="small" />}
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        {t('reports.detailed.open')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data && data.count > PAGE_SIZE && (
          <Box>
            <TablePagination
              component="div"
              count={data.count}
              page={page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_e, p) => setPage(p)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportOrdersDialog;
